  import React, { useState, useEffect } from "react";
  import axios from "axios";
  import { ToastContainer, toast } from "react-toastify";
  import "react-toastify/dist/ReactToastify.css";
  import "../assets/SavingScheme.css";

  const API_BASE = "https://payyug.in/api";

  const SavingScheme = () => {
    const [schemeStatus, setSchemeStatus] = useState(false);
    const [formData, setFormData] = useState({
      accountType: "Saving Account",
      serviceName: "",
      minOpeningBalance: "",
      preclosurePeriod: "",
      preclosureChargeType: "",
      preclosureChargeValue: "",
      dailyCashLimit: "",
      dailyFundLimit: "",
      locinAmount: "",
      minMonthlyAvgBalance: "",
      nonMaintenanceCharge: "",
      roi: "",
      roiFrequency: "Monthly",
      interestPayoutFrequency: "",
      prefix: "",
      serviceChargeFrequency: "",
      serviceCharge: "",
      smsChargeFrequency: "",
      smsCharge: "",
    });

    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [schemes, setSchemes] = useState([]);
    const [editingId, setEditingId] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const adminId = localStorage.getItem("adminuserId");

    // ✅ Toast notification system
    useEffect(() => {
      if (message) {
        if (message.startsWith("✅")) {
          toast.success(message, { position: "top-center", autoClose: 2500, theme: "colored" });
        } else if (message.startsWith("⚠️")) {
          toast.warning(message, { position: "top-center", autoClose: 3000, theme: "colored" });
        } else if (message.startsWith("❌")) {
          toast.error(message, { position: "top-center", autoClose: 3000, theme: "colored" });
        } else if (message.startsWith("🗑️")) {
          toast.info(message, { position: "top-center", autoClose: 2500, theme: "colored" });
        } else {
          toast(message, { position: "top-center", autoClose: 2500 });
        }
      }
    }, [message]);

    // ✅ Handle Input Change
    const handleChange = (e) => {
      const { name, value } = e.target;
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    };

    // ✅ Fetch All Schemes (Filtered by superAdminId)
    const fetchSchemes = async (page = 1) => {
      try {
        const superAdminId = localStorage.getItem("adminuserId");
        if (!superAdminId) {
          console.error("❌ superAdminId missing from localStorage");
          return;
        }

        const res = await axios.get(`${API_BASE}/get-all-saving-scheme?superAdminId=${superAdminId}`);
        if (res.data.success) {
          setSchemes(res.data.data || []);
          setTotalPages(res.data.totalPages || 1);
        } else {
          console.error("⚠️ Failed to fetch saving schemes:", res.data.message);
        }
      } catch (err) {
        console.error("Error fetching saving schemes:", err);
      }
    };

    useEffect(() => {
      fetchSchemes(currentPage);
    }, [currentPage]);

    // ✅ Handle Submit (Create / Update)
    const handleSubmit = async (e) => {
      e.preventDefault();
      setLoading(true);
      setMessage("");

      const payload = {
        ...formData,
        superAdminId: adminId,
        status: schemeStatus ? "Active" : "Inactive",
      };

      try {
        const res = editingId
          ? await axios.put(`${API_BASE}/update-saving-scheme/${editingId}`, payload)
          : await axios.post(`${API_BASE}/create-saving-scheme`, payload);

        if (res.data.success) {
          setMessage(
            editingId
              ? "✅ Saving Scheme Updated Successfully!"
              : "✅ Saving Scheme Created Successfully!"
          );
          fetchSchemes(currentPage);
          resetForm();
        } else {
          setMessage("⚠️ Failed to save scheme. Please try again.");
        }
      } catch (error) {
        console.error("Error:", error);
        setMessage("❌ Error: " + (error.response?.data?.message || error.message));
      } finally {
        setLoading(false);
      }
    };

    // ✅ Reset Form
    const resetForm = () => {
      setFormData({
        accountType: "Saving Account",
        serviceName: "",
        minOpeningBalance: "",
        preclosurePeriod: "",
        preclosureChargeType: "",
        preclosureChargeValue: "",
        dailyCashLimit: "",
        dailyFundLimit: "",
        locinAmount: "",
        minMonthlyAvgBalance: "",
        nonMaintenanceCharge: "",
        roi: "",
        roiFrequency: "Monthly",
        interestPayoutFrequency: "",
        prefix: "",
        serviceChargeFrequency: "",
        serviceCharge: "",
        smsChargeFrequency: "",
        smsCharge: "",
      });
      setSchemeStatus(false);
      setEditingId(null);
    };

    // ✅ Edit Scheme
    const handleEdit = (scheme) => {
      const superAdminId = localStorage.getItem("adminuserId") || "";
      setEditingId(scheme.id);
      setFormData({
        accountType: scheme.accountType,
        serviceName: scheme.serviceName,
        minOpeningBalance: scheme.minOpeningBalance,
        preclosurePeriod: scheme.preclosurePeriod,
        preclosureChargeType: scheme.preclosureChargeType,
        preclosureChargeValue: scheme.preclosureChargeValue,
        dailyCashLimit: scheme.dailyCashLimit,
        dailyFundLimit: scheme.dailyFundLimit,
        locinAmount: scheme.locinAmount,
        minMonthlyAvgBalance: scheme.minMonthlyAvgBalance,
        nonMaintenanceCharge: scheme.nonMaintenanceCharge,
        roi: scheme.roi,
        roiFrequency: scheme.roiFrequency,
        interestPayoutFrequency: scheme.interestPayoutFrequency,
        prefix: scheme.prefix,
        serviceChargeFrequency: scheme.serviceChargeFrequency,
        serviceCharge: scheme.serviceCharge,
        smsChargeFrequency: scheme.smsChargeFrequency,
        smsCharge: scheme.smsCharge,
        superAdminId: superAdminId,
      });
      setSchemeStatus(scheme.status === "Active");
      window.scrollTo({ top: 0, behavior: "smooth" });
    };

    // ✅ Delete Scheme
    const handleDelete = async (id) => {
      if (!window.confirm("Are you sure you want to delete this scheme?")) return;
      try {
        const res = await axios.delete(`${API_BASE}/delete-saving-scheme/${id}`);
        if (res.data.success) {
          setMessage("🗑️ Scheme deleted successfully!");
          fetchSchemes(currentPage);
        } else {
          setMessage("⚠️ Failed to delete scheme.");
        }
      } catch (err) {
        console.error("Error deleting scheme:", err);
        setMessage("❌ Failed to delete scheme.");
      }
    };

    return (
      <div className="SavingScheme">
        <h2 className="SavingSchemetitle">Saving Account Scheme</h2>
        {/* <p className="SavingSchemesubtitle">Create or manage saving schemes.</p> */}

        {/* =============== FORM =============== */}
        <form className="SavingScheme-form" onSubmit={handleSubmit}>
          <div className="form-grid">
            {[
              { label: "Account Type", name: "accountType", type: "select", options: ["Saving Account", "Current Account"] },
              { label: "Service Name", name: "serviceName", type: "text" },
              { label: "Minimum Opening Balance", name: "minOpeningBalance", type: "number" },
              { label: "Preclosure Period (in months)", name: "preclosurePeriod", type: "text" },
              { label: "Preclosure Type", name: "preclosureChargeType", type: "select", options: ["Fixed", "Percentage"] },
              { label: "Preclosure Charge", name: "preclosureChargeValue", type: "number" },
              { label: "Daily Cash Limit", name: "dailyCashLimit", type: "number" },
              { label: "Daily Fund Limit", name: "dailyFundLimit", type: "number" },
              { label: "Locin Amount", name: "locinAmount", type: "number" },
              { label: "Min Monthly Avg Balance", name: "minMonthlyAvgBalance", type: "number" },
              { label: "Non Maintenance Charge", name: "nonMaintenanceCharge", type: "number" },
              { label: "ROI (%)", name: "roi", type: "number" },
              { label: "ROI Frequency", name: "roiFrequency", type: "select", options: ["Monthly", "Quarterly","Half Yearly","Yearly"] },
              { label: "Interest Payout Frequency", name: "interestPayoutFrequency", type: "select", options: ["Monthly", "Quarterly","Half Yearly","Yearly"] },
              { label: "Prefix", name: "prefix", type: "text" },
              { label: "Service Charge Type", name: "serviceChargeFrequency", type: "select", options: ["Monthly","Quarterly","Half Yearly","Yearly"] },
              { label: "Service Charge Fee", name: "serviceCharge", type: "number" },
              { label: "SMS Charge Type", name: "smsChargeFrequency", type: "select", options: ["Monthly", "Yearly"] },
              { label: "SMS Charge Fee", name: "smsCharge", type: "number" },
              { label: "Bank Statement Fee", name: "bankStatment", type: "number" },
              { label: "New Passbook Fee", name: "newPassbook", type: "number" },
            ].map((f) => (
              <div key={f.name} className="Saving-scheme-form-field">
                <label>{f.label}</label>
                {f.type === "select" ? (
                  <select
                    name={f.name}
                    value={formData[f.name]}
                    onChange={handleChange}
                    className="Saving-scheme-form-select"
                  >
                    {f.options.map((opt) => (
                      <option key={opt}>{opt}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type={f.type}
                    name={f.name}
                    className="Saving-scheme-form-input"
                    value={formData[f.name]}
                    onChange={handleChange}
                    // placeholder={`Enter ${f.label.toLowerCase()}`}
                  />
                )}
              </div>
            ))}

            {/* Toggle */}
            <div className="form-field toggle-field">
              <label>Scheme Status</label>
              <label className="switch-premium">
                <input
                  type="checkbox"
                  checked={schemeStatus}
                  onChange={() => setSchemeStatus(!schemeStatus)}
                />
                <span className="slider-premium"></span>
              </label>
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="saving-scheme-btn-primary" disabled={loading}>
              {loading ? "Submitting..." : editingId ? "Update Scheme" : "Submit"}
            </button>
            {editingId && (
              <button type="button" className="btn-secondary" onClick={resetForm}>
                Cancel Edit
              </button>
            )}
          </div>
        </form>

        {/* =============== REPORT TABLE =============== */}
        <div className="SavingScheme-report-wrapper">
          <h3>Saving Account Scheme Report</h3>

          <table className="SavingScheme-report-table">
            <thead>
              <tr>
                <th>Sr No.</th>
                <th>Service Name</th>
                <th>Prefix</th>
                <th>ROI</th>
                <th>Min Balance</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {schemes.length > 0 ? (
                schemes.map((scheme, i) => (
                  <tr key={scheme.id}>
                    <td>{i + 1}</td>
                    <td>{scheme.serviceName||"N/A"}</td>
                    <td>{scheme.prefix||"N/A"}</td>
                    <td>{`${scheme.roi}%`||"N/A"}</td>
                    <td>{scheme.minOpeningBalance||"N/A"}</td>
                    <td>{scheme.status||"N/A"}</td>
                    <td>
                      <div className="saving-scheme-btns">
                      <button className="saving-scheme-btn-edit" onClick={() => handleEdit(scheme)}>
                        ✏️
                      </button>
                      <button className="saving-scheme-btn-delete" onClick={() => handleDelete(scheme.id)}>
                        🗑️
                      </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" style={{ textAlign: "center" }}>
                    No data available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* ✅ Toast container */}
        <ToastContainer />
      </div>
    );
  };

  export default SavingScheme;






















































































  // import React, { useRef } from "react";
  // import jsPDF from "jspdf";
  // import html2canvas from "html2canvas";
  // import "../assets/MembershipForm.css";

  // const SavingScheme = () => {
  //   const formRef = useRef();

  //   const handleDownloadPDF = async () => {
  //     const input = formRef.current;
  //     const canvas = await html2canvas(input, { scale: 2, useCORS: true });
  //     const imgData = canvas.toDataURL("image/png");
  //     const pdf = new jsPDF("p", "mm", "a4");
  //     const imgWidth = 210;
  //     const imgHeight = (canvas.height * imgWidth) / canvas.width;
  //     pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);
  //     pdf.save("Membership_Form.pdf");
  //   };

  //   return (
  //     <div className="membership-container">
  //       <div ref={formRef} className="membership-form">
  //         {/* Header */}
  //         <div className="header-top">
  //           <div className="header-left">
  //             <img
  //               src="https://payyug.in/uploads/1760015144150-WhatsApp_Image_2025-10-09_at_5.46.49_PM-removebg-preview.png"
  //               alt="BANKSAN Logo"
  //               className="logo"
  //             />
  //           </div>
  //           <div className="header-center">
  //             <h1>BANKSAN NIDHI LIMITED</h1>
  //             <p>
  //               Regd./Head Office: Hanuman Market, Kunwar Singh Colony, Chas - 827013,
  //               Bokaro (Jharkhand)
  //             </p>
  //             <p>
  //               Website: <b>https://asbflindia.in</b> | E-mail: info@asbflindia.in
  //             </p>
  //             {/* <p className="tagline">Apka Bharose Ka Bank</p> */}
  //           </div>
  //         </div>

  //         <div className="red-bar">MIS Cirtificate</div>

        





  //         {/* Branch details */}
  //         <div className="branch-line">
  //           <span>Branch Name: ___________________________</span>
  //           <span>Branch Code: ___________________________</span>
  //         </div>

  //         <p className="to-text">
  //           To,<br />
  //           The Chief Executive Officer/Branch Manager<br />
  //           BANKSAN NIDHI LTD.<br />
  //           Bokaro, Jharkhand
  //         </p>

  //         <p className="small-text">(please fill in BLOCK letters)</p>

  //         <p className="intro-line">
  //           I, Sri/Smt/Miss __________________________________ opt to be Member in “BANKSAN
  //           NIDHI LTD.” and my detailed particulars are as follows:
  //         </p>




  //           {/* 🔹 Form Info beside Photo */}
  //         <div className="form-info-section">
  //           <table className="form-info-table">
  //             <tbody>
  //               <tr>
  //                 <td><b>Form No:</b></td>
  //                 <td> 00979</td>
  //               </tr>
  //               <tr>
  //                 <td><b>Scheme:</b></td>
  //                 <td>MIS Deposit</td>
  //               </tr>
  //               <tr>
  //                 <td><b>Maturity Amount:</b></td>
  //                 <td>  3,50,000 </td>
  //               </tr>
  //               <tr>
  //                 <td><b>Tenure:</b></td>
  //                 <td>5 Year</td>
  //               </tr>
  //               <tr>
  //                 <td><b>ROI:</b></td>
  //                 <td>10.5%</td>
  //               </tr>
  //               <tr>
  //                 <td><b>Maturity Date:</b></td>
  //                 <td>30/09/2030</td>
  //               </tr>
  //             </tbody>
  //           </table>
  //           <div className="photo-box">PHOTO</div>
  //         </div>



  //         {/* Personal Info */}
  //         <h3 className="section-title">PERSONAL INFORMATION</h3>

  //         <table className="detail-table">
  //           <tbody>
  //             <tr>
  //               <td>Name of Applicant:</td>
  //               <td>SANTOSH KUMAR VISHWAKARMA</td>
  //             </tr>
  //             <tr>
  //               <td>Father’s/Husband’s Name:</td>
  //               <td>BAIJNATH MISTRY</td>
  //             </tr>
  //             <tr>
  //               <td>Present Address:</td>
  //               <td>A-100, Duhatand Nagar Nigam, Dhanbad, Jharkhand, 826106</td>
  //             </tr>
  //             <tr>
  //               <td>Date of Birth:</td>
  //               <td>15/02/1978</td>
  //               <td>Age:</td>
  //               <td>48</td>
  //             </tr>
  //             <tr>
  //               <td>Gender:</td>
  //              <td>      Male                 </td>
  //               <td>Marital Status:</td>
  //               <td>   Married                    </td>
  //             </tr>
  //             <tr>
  //               <td>Aadhaar No:</td>
  //                <td>  BZ98532087          </td>
  //               <td>PAN No:</td>
  //               <td>AJCPV1990R</td>
  //             </tr>
  //             <tr>
  //               <td>Mobile No:</td>
  //               <td>9955948192</td>
  //             </tr>
  //           </tbody>
  //         </table>

  //         {/* Nominee Info */}
  //         <h3 className="section-title">NOMINEE DETAILS</h3>
  //         <table className="detail-table">
  //           <tbody>
  //             <tr>
  //               <td>Name:</td>
  //               <td>AJIT VISHWAKARMA</td>
  //             </tr>
  //             <tr>
  //               <td>Age:</td>
  //               <td>46</td>
  //               <td>Relationship:</td>
  //               <td>Bhatija</td>
  //             </tr>
  //           </tbody>
  //         </table>

  //         {/* Introducer Section */}
  //         <h3 className="section-title">INTRODUCER DETAILS</h3>
  //         <table className="detail-table">
  //           <tbody>
  //             <tr>
  //               <td>Name and Address:</td>
  //               <td colSpan="3"></td>
  //             </tr>
  //             <tr>
  //               <td>Membership ID:</td>
  //               <td></td>
  //               <td>Signature of Introducer:</td>
  //               <td></td>
  //             </tr>
  //           </tbody>
  //         </table>

  //         <div className="signatures">
  //           <p>Signature of Applicant: __________________________</p>
  //         </div>
  //         <div className="signatures">
  //           <p>Signature of Authorize: __________________________</p>
  //         </div>
  //       </div>

  //       <button onClick={handleDownloadPDF} className="download-btn">
  //         📄 Download PDF
  //       </button>
  //     </div>
  //   );
  // };

  // export default SavingScheme;


  // /* ---------- Container ---------- */


  // /*

  // .membership-container {
  //   display: flex;
  //   flex-direction: column;
  //   align-items: center;
  //   background-color: #f6f5e8;
  //   font-family: "Times New Roman", serif;
  //   color: #000;
  // }
  //   */

  // /* ---------- Main Form ---------- */
  // .membership-form {
  //   background-color: #fff8dc; /* light cream paper */
  //   width: 800px;
  //   padding: 25px 30px;
  //   border: 1px solid #999;
  //   box-sizing: border-box;
  // }


  // */
  // /* ---------- Header Section ---------- */
  // .header-top {
  //   display: flex;
  //   justify-content: space-between;
  //   align-items: flex-start;
  //   border-bottom: 2px solid #d62828;
  //   padding-bottom: 10px;
  //   background: #fff8dc !important;
  // }
  // */

  // .logo {
  //   width: 90px;
  // }
  // */

  // .header-center {
  //   flex: 1;
  //   text-align: center;
  // }
  // */

  // .header-center h1 {
  //   color: #d62828;
  //   margin: 0;
  //   font-size: 22px;
  // }
  // */

  // .header-center p {
  //   margin: 2px 0;
  // }
  // */

  // .tagline {
  //   font-weight: bold;
  //   font-size: 14px;
  //   margin-top: 5px;
  // }

  // */
  // /* ---------- Red Title Bar ---------- */
  // .red-bar {
  //   background: #d62828;
  //   color: #fff;
  //   text-align: center;
  //   font-weight: bold;
  //   padding: 6px;
  //   margin-top: 8px;
  //   margin-bottom: 10px;
  //   font-size: 15px;
  // }

  // */
  // /* ---------- Form Info + Photo Section ---------- */
  // .form-info-section {
  //   display: flex;
  //   justify-content: flex-end;
  //   align-items: flex-start;
  //   gap: 20px;
  //   margin-bottom: 10px;
  // }
  // */

  // .form-info-table {
  //   border-collapse: collapse;
  //   width: 220px;
  // }
  // */

  // .form-info-table td {
  //   border: 1px solid #888;
  //   padding: 4px 8px;
  //   font-size: 14px;
  //   height: 22px;
  //   background-color: #fff8dc;
  // }
  // */

  // /* ---------- Photo Box ---------- */
  // .photo-box {
  //   width: 100px;
  //   height: 120px;
  //   border: 1px solid #000;
  //   text-align: center;
  //   font-size: 12px;
  //   padding-top: 45px;
  //   background: #fff;
  // }
  // */

  // /* ---------- Branch Info Line ---------- */
  // .branch-line {
  //   display: flex;
  //   justify-content: space-between;
  //   font-size: 14px;
  //   margin-bottom: 10px;
  // }

  // */
  // /* ---------- Text Styles ---------- */
  // .to-text,
  // .small-text,
  // .intro-line {
  //   font-size: 14px;
  //   margin-bottom: 8px;
  // }


  // */
  // .small-text {
  //   text-align: center;
  //   font-style: italic;
  // }

  // */

  // /* ---------- Section Titles ---------- */
  // .section-title {
  //   color: #002060;
  //   font-weight: bold;
  //   font-size: 15px;
  //   border-bottom: 1px solid #002060;
  //   margin-top: 12px;
  //   margin-bottom: 6px;
  // }

  // */
  // /* ---------- Detail Tables ---------- */
  // .detail-table {
  //   width: 100%;
  //   border-collapse: collapse;
  //   margin-bottom: 15px;
  //   table-layout: fixed; /* consistent column widths */
  // }


  // */
  // .detail-table td {
  //   border: 1px solid #888;
  //   padding: 6px 8px;
  //   font-size: 14px;
  //   vertical-align: top;
  //   word-wrap: break-word;
  // }

  // /* Key–Value Column Alignment */
  // .detail-table td:first-child,
  // .detail-table td:nth-child(3) {
  //   width: 25%;
  //   font-weight: bold;
  //   white-space: nowrap;
  // }

  // .detail-table td:nth-child(2),
  // .detail-table td:nth-child(4) {
  //   width: 35%;
  // }

  // /* ---------- Signatures ---------- */
  // .signatures {
  //   font-size: 14px;
  //   margin-top: 15px;
  //   line-height: 1.8;
  // }

  // /* ---------- Download Button ---------- */
  // .download-btn {
  //   margin-top: 20px;
  //   background-color: #d62828;
  //   color: white;
  //   border: none;
  //   padding: 10px 25px;
  //   border-radius: 5px;
  //   font-size: 16px;
  //   cursor: pointer;
  //   transition: background-color 0.3s ease;
  // }

  // .download-btn:hover {
  //   background-color: #a91d1d;
  // }

